import { useMemo } from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { AnyChatForm, NumOfBlocks, SortableCard, SortableRoot } from '@anyx/feature-crm/shared';

import { AddLineMessageBlock } from '../add-line-message-block';
import { BlockTitle } from '../block-title';
import { useLineMessageStore } from '../store';
import { LineMessageBlock, LineMessageFormFieldsProps, LineMessageBlockType } from '../types';
import { getDefaultBlockByType } from '../utils';

const MAX_MESSAGE_BLOCKS = 5;

type MessageBlocksProps = {
  appendBlockByType?: (type: LineMessageBlockType) => LineMessageBlock | undefined;
};

export function MessageBlocks({ appendBlockByType }: MessageBlocksProps) {
  const { t } = useTranslation('crm');
  const navigateToBlockView = useLineMessageStore((state) => state.navigateToBlockView);

  const isFormDisabled = useLineMessageStore((state) => state.isFormDisabled);

  const {
    control,
    formState: { errors },
  } = useFormContext<LineMessageFormFieldsProps>();

  const { fields, append, move, remove } = useFieldArray({
    control,
    name: 'messages',
  });

  const fieldsId = useMemo(() => {
    return fields.map(({ id }) => id);
  }, [fields]);

  const handleAddBlock = (type: LineMessageBlockType) => {
    append(appendBlockByType?.(type) || getDefaultBlockByType(type));

    // Without setTimeout, after navigate automatically, the input field will autofocus.
    setTimeout(() => {
      navigateToBlockView(type, fields.length);
    }, 50);
  };

  return (
    <AnyChatForm.Block>
      {fields.length > 0 && (
        <SortableRoot className="mt-5 space-y-2" onMove={move} itemsId={fieldsId}>
          {fields.map((field, index) => {
            return (
              <SortableCard
                key={`${field.type}-${index}`}
                id={field.id}
                disabled={isFormDisabled}
                disableRemove={fields.length <= 1}
                hideRemove={isFormDisabled}
                error={!!errors.messages?.[index]}
                render={<BlockTitle type={field.type} />}
                deleteTooltip={t('crm.lineMessageForm.actions.deleteBlock')}
                onClick={() => {
                  navigateToBlockView(field.type, index);
                }}
                onRemove={() => {
                  remove(index);
                }}
              />
            );
          })}
        </SortableRoot>
      )}

      {!isFormDisabled && (
        <div className="flex w-max flex-col gap-y-1">
          {fields.length < MAX_MESSAGE_BLOCKS && <AddLineMessageBlock onAdd={handleAddBlock} />}
          <NumOfBlocks current={fields.length} max={MAX_MESSAGE_BLOCKS} />
        </div>
      )}
    </AnyChatForm.Block>
  );
}
